import React, { useEffect, useRef, useState } from 'react';
import * as monaco from 'monaco-editor';
import MonacoEditor, { loader } from '@monaco-editor/react';

loader.config({ monaco });

export const SPQLEditor = ({
  fieldsMetadata,
  blurAction,
  submit,
  currentSorting,
  ...props
}) => {
  const monacoRef = useRef(null);
  const editorRef = useRef(null);
  const [completionDiposable, setCompletionDisposable] = useState(null);

  useEffect(() => {
    return () => {
      if (completionDiposable) {
        completionDiposable.dispose();
      }
    };
  }, [completionDiposable]);

  useEffect(() => {
    if (!monacoRef.current || !editorRef.current) return;
    editorRef.current.addAction({
      id: 'trigger-search',
      label: 'Trigger Search',
      keybindings: [monacoRef.current.KeyCode.Enter],
      run: function (ed) {
        submit(ed.getValue(), currentSorting);
      },
    });
  }, [currentSorting]);

  const handleEditorWillMount = (monaco) => {
    monacoRef.current = monaco;
    monaco.languages.register({ id: 'spql' });
    const keys = Object.keys(fieldsMetadata);

    monaco.languages.setMonarchTokensProvider('spql', {
      tokenizer: {
        root: [
          [
            /(\bAND\b)|(\band\b)|("[^"]*")/,
            {
              cases: {
                $1: 'operator',
                $2: 'operator',
                $3: 'value',
              },
            },
          ],
          ...keys.map((keyword) => [new RegExp(`\\b${keyword}\\b`), 'keyword']),
        ],
      },
    });

    monaco.editor.defineTheme('spqlTheme', {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'keyword', foreground: '#7cff00' },
        { token: 'operator', foreground: '#ED634E' },
      ],
      colors: {
        'editor.foreground': '#F8F8F8',
        'editor.background': '#1C3138',
        'editor.selectionBackground': '#DDF0FF33',
        'editor.lineHighlightBackground': '#FFFFFF08',
        'editorCursor.foreground': '#A7A7A7',
        'editorWhitespace.foreground': '#FFFFFF40',
      },
    });

    monaco.languages.setLanguageConfiguration('spql', {
      brackets: [['[', ']']],
    });

    const completionProvider = monaco.languages.registerCompletionItemProvider(
      'spql',
      {
        provideCompletionItems: () => {
          const suggestions = keys.map((keyword) => ({
            label: keyword,
            kind: monaco.languages.CompletionItemKind.Keyword,
            insertText: keyword,
          }));
          return { suggestions };
        },
      },
    );

    setCompletionDisposable(completionProvider);
  };
  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
    editor.addAction({
      id: 'trigger-search',
      label: 'Trigger Search',
      keybindings: [monaco.KeyCode.Enter],
      run: function (ed) {
        submit(ed.getValue(), currentSorting);
      },
    });
    editor.onDidBlurEditorWidget(() => {
      blurAction(editor.getValue());
    });
    const updatePadding = () => {
      editor.changeViewZones((changeAccessor) => {
        changeAccessor.addZone({
          afterLineNumber: 0,
          heightInPx: 7,
          domNode: document.createElement('span'),
        });
      });
    };

    updatePadding();
  };

  return (
    <MonacoEditor
      onMount={handleEditorDidMount}
      beforeMount={handleEditorWillMount}
      language="spql"
      theme="spqlTheme"
      value={props.value}
      {...props}
    />
  );
};
